import { useQuery } from "@tanstack/react-query";
import { axios } from "./axios";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { ROUTE } from "../routes";
import { Team } from "../types/Team";
import { env } from "../utils/env";

const teamParams = (division?: number | number[]) => {
  if (Array.isArray(division)) {
    return { all: true, division__in: division.join(",") };
  }
  return division ? { all: true, division } : { all: true };
};

const fetchTeams = (authHeader: string, division?: number | number[]) => () =>
  axios.get<Team[]>(`${env.verifyServiceUrl}common/team/`, {
    headers: {
      Authorization: authHeader,
    },
    params: teamParams(division),
  });

/**
 * Fetches teams for the tenant. Pass one Division id to scope the request
 * server-side (`?division=`), an array to fetch the teams of several Divisions
 * in one call (`?division__in=1,2`), or omit it to fetch every team. The array
 * form is keyed on its sorted ids, so callers needn't memoise it.
 */
export const useTeams = (division?: number | number[], enabled = true) => {
  const { authHeader } = useAuth();
  const navigate = useNavigate();

  const divisionKey = Array.isArray(division)
    ? [...division].sort((a, b) => a - b).join(",")
    : division;

  return useQuery(["teams", divisionKey], fetchTeams(authHeader, division), {
    enabled,
    retry: false,
    onError: (err: any) => {
      if (err.request?.status === 500 || err.request?.status === 503) {
        navigate(ROUTE.SERVER_ERROR);
      }
      if (err.request?.status === 401) {
        navigate(ROUTE.LOGOUT);
      }
    },
  });
};
